import React from "react";
import { FormProvider } from "react-hook-form";
import { 
  MessageSquare,
  Activity,
  StickyNote,
  Stethoscope,
  Search,
  Pill,
  FileText,
  Calendar
} from "lucide-react";

// Import modular pieces
import { usePrescriptionForm } from "@/hooks/usePrescriptionForm";
import { usePrescriptionSections } from "@/hooks/usePrescriptionSections";
import FormSectionWrapper from "@/components/PrescriptionForm/FormSectionWrapper";
import FormActionBar from "@/components/PrescriptionForm/StickyBar";
import { PrescriptionFormData } from "@/types/prescription";

// Import existing section components
import ComplaintsGrid from "@/components/PrescriptionFormSections/ComplaintsGrid";
import VitalsForm from "@/components/PrescriptionFormSections/VitalsForm";
import QuickNotesSection from "@/components/PrescriptionFormSections/QuickNotesSection";
import DiagnosisSection from "@/components/PrescriptionFormSections/DiagnosisSection";
import InvestigationsGrid from "@/components/PrescriptionFormSections/InvestigationsGrid";
import MedicineListForm from "@/components/PrescriptionFormSections/MedicineListForm";
import AdviceSection from "@/components/PrescriptionFormSections/AdviceSection";
import NextVisitSection from "@/components/PrescriptionFormSections/NextVisitSection";

// Import template modals
import LoadTemplateModal from "@/components/Template/LoadTemplateModal";
import SaveTemplateModal from "@/components/Template/SaveTemplateModal";

interface FormSection {
  id: string;
  icon: React.ReactNode;
  label: string;
  content: React.ReactNode;
  hasTemplate?: boolean;
  hasClear?: boolean;
  required?: boolean;
}

interface PrescriptionFormProps {
  mode?: "create" | "edit";
  patientId: string;
  initialData?: Partial<PrescriptionFormData>;
  onSaved?: () => void;
  onCancel?: () => void;
  showStickyBar?: boolean;
  onFormReady?: (submitFn: () => void, hasChanges: boolean, isSubmitting: boolean) => void;
}

export default function PrescriptionForm({
  mode = "create",
  patientId,
  initialData,
  onSaved,
  onCancel,
  showStickyBar = true,
  onFormReady
}: PrescriptionFormProps) {
  
  // Use modular hooks
  const formHook = usePrescriptionForm({
    mode,
    patientId,
    initialData,
    onSaved,
    onCancel
  });

  const sectionsHook = usePrescriptionSections();

  // Notify parent about form readiness
  React.useEffect(() => {
    if (onFormReady) {
      onFormReady(formHook.submitForm, formHook.hasChanges, formHook.isSubmitting);
    }
  }, [onFormReady, formHook.submitForm, formHook.hasChanges, formHook.isSubmitting]);

  // Handle section clearing
  const handleClearSection = (sectionId: string, sectionLabel: string) => {
    const result = sectionsHook.handleClearSection(sectionId, sectionLabel);
    if (!result) return;

    // Clear the specific section based on ID
    switch(sectionId) {
      case 'complaints':
        formHook.form.setValue('complaints', []);
        break;
      case 'vitals':
        formHook.form.setValue('vitals', {});
        break;
      case 'diagnosis':
        formHook.form.setValue('diagnosis', '');
        break;
      case 'medicines':
        formHook.form.setValue('medicines', []);
        break;
      case 'investigations':
        formHook.form.setValue('investigations', []);
        break;
      case 'advice':
        formHook.form.setValue('advice', '');
        break;
      case 'notes':
        formHook.form.setValue('quick_notes', '');
        break;
      case 'next-visit':
        formHook.form.setValue('next_visit', {});
        break;
    }
  };

  // Handle template application
  const handleApplyTemplate = (template: any) => {
    const type = sectionsHook.activeLoadType;
    if (!type || !template.value) return;

    // Apply template data to the appropriate form section
    switch(type) {
      case 'complaint':
        if (Array.isArray(template.value)) {
          // Ensure each complaint has the correct structure
          const formattedComplaints = template.value.map((item: any) => {
            if (typeof item === 'string') {
              return {
                complaint: item,
                frequency: "",
                severity: "",
                duration: "",
                date: ""
              };
            } else if (typeof item === 'object') {
              return {
                complaint: item.complaint || "",
                frequency: item.frequency || "",
                severity: item.severity || "",
                duration: item.duration || "",
                date: item.date || ""
              };
            }
            return item;
          });
          
          // Use setValue with shouldDirty option to trigger form updates
          formHook.form.setValue('complaints', formattedComplaints, {
            shouldDirty: true,
            shouldTouch: true
          });
        }
        break;
      case 'medicine':
        if (Array.isArray(template.value)) {
          const mapped = (template.value as { product?: string; brand_name?: string; generic_name?: string; dosage?: string; when?: string; taken_when?: string; duration?: string; quantity?: string | number; notes?: string }[]).map(m => ({
            drug_id: null,
            product: m.brand_name || m.product || "",
            generic_name: m.generic_name || "",
            dosage: m.dosage ?? "",
            taken_when: m.taken_when ?? m.when ?? "",
            duration: m.duration ?? "",
            quantity: m.quantity ?? "",
            notes: m.notes ?? ""
          }));
          formHook.form.setValue('medicines', mapped, {
            shouldDirty: true,
            shouldTouch: true
          });
        }
        break;
      case 'investigation':
        if (Array.isArray(template.value)) {
          formHook.form.setValue('investigations', template.value);
        }
        break;
      case 'diagnosis':
        if (typeof template.value?.value === 'string') {
          formHook.form.setValue('diagnosis', template.value.value, {
            shouldDirty: true,
            shouldTouch: true
          });
        }
        break;
      case 'advice':
        if (typeof template.value?.value === 'string') {
          formHook.form.setValue('advice', template.value.value, {
            shouldDirty: true,
            shouldTouch: true
          });
        }
        break;
      case 'quick_notes':
        if (typeof template.value?.value === 'string') {
          formHook.form.setValue('quick_notes', template.value.value, {
            shouldDirty: true,
            shouldTouch: true
          });
        }
        break;
    }
    
    sectionsHook.closeLoadTemplate();
  };

  // Handle template saving data collection
  const getTemplateData = (type: string) => {
    const values = formHook.form.getValues();
    switch(type) {
      case 'complaint': return values.complaints;
      case 'medicine': return values.medicines;
      case 'investigation': return values.investigations;
      case 'diagnosis': return values.diagnosis;
      case 'advice': return values.advice;
      case 'quick_notes': return values.quick_notes;
      default: return null;
    }
  };

  // Form sections configuration
  const sections: FormSection[] = [
    { 
      id: "complaints",
      icon: <MessageSquare className="w-4 h-4" />, 
      label: "Complaints", 
      content: <ComplaintsGrid />, 
      hasTemplate: true,
      hasClear: true,
      required: true
    },
    { 
      id: "vitals",
      icon: <Activity className="w-4 h-4" />, 
      label: "Vital Signs", 
      content: <VitalsForm />,
      hasClear: true
    },
    { 
      id: "notes",
      icon: <StickyNote className="w-4 h-4" />, 
      label: "Quick Notes", 
      content: <QuickNotesSection />,
      hasTemplate: true,
      hasClear: true
    },
    { 
      id: "diagnosis",
      icon: <Stethoscope className="w-4 h-4" />, 
      label: "Diagnosis", 
      content: <DiagnosisSection />,
      hasTemplate: true,
      hasClear: true,
      required: true
    },
    { 
      id: "investigations",
      icon: <Search className="w-4 h-4" />, 
      label: "Investigations", 
      content: <InvestigationsGrid />,
      hasTemplate: true,
      hasClear: true
    },
    { 
      id: "medicines",
      icon: <Pill className="w-4 h-4" />, 
      label: "Medications", 
      content: <MedicineListForm />,
      hasTemplate: true,
      hasClear: true
    },
    { 
      id: "advice",
      icon: <FileText className="w-4 h-4" />, 
      label: "Advice", 
      content: <AdviceSection />,
      hasTemplate: true,
      hasClear: true
    },
    { 
      id: "next-visit",
      icon: <Calendar className="w-4 h-4" />, 
      label: "Next Visit", 
      content: <NextVisitSection />,
      hasClear: true
    }
  ];

  // Check for section errors
  const getSectionErrors = (sectionId: string) => {
    switch(sectionId) {
      case 'complaints': return !!formHook.errors.complaints;
      case 'vitals': return !!formHook.errors.vitals;
      case 'diagnosis': return !!formHook.errors.diagnosis;
      case 'medicines': return !!formHook.errors.medicines;
      case 'investigations': return !!formHook.errors.investigations;
      case 'advice': return !!formHook.errors.advice;
      case 'notes': return !!formHook.errors.quick_notes;
      case 'next-visit': return !!formHook.errors.next_visit;
      default: return false;
    }
  };

  return (
    <FormProvider {...formHook.form}>
      <div className="space-y-2 bg-background">

        {/* Improved UX: Better Template Access & Visual Hierarchy */}
        <div className="space-y-2">
          {sections.map((section) => (
            <FormSectionWrapper
              key={section.id}
              section={section}
              isMinimized={sectionsHook.minimizedSections[section.id] || false}
              hasErrors={getSectionErrors(section.id)}
              sectionCount={sectionsHook.getSectionCount(section.id, formHook.watchedValues)}
              hasData={sectionsHook.getSectionStatus(section.id, formHook.watchedValues)}
              onMinimize={sectionsHook.handleMinimizeSection}
              onExpand={sectionsHook.handleExpandSection}
              onLoadTemplate={sectionsHook.handleLoadTemplate}
              onSaveTemplate={sectionsHook.handleSaveTemplate}
              onClearSection={handleClearSection}
            />
          ))}
        </div>

        {/* Sticky Save Bar */}
        {showStickyBar && (
          <FormActionBar
            hasChanges={formHook.hasChanges}
            hasValidChanges={Boolean(formHook.hasValidChanges)}
            isSubmitting={formHook.isSubmitting}
            mode={formHook.mode}
            onSubmit={formHook.submitForm}
            onCancel={formHook.handleCancel}
          />
        )}

        {/* Template Modals */}
        {sectionsHook.activeLoadType && (
          <LoadTemplateModal
            open={sectionsHook.activeLoadType !== null}
            type={sectionsHook.activeLoadType}
            onClose={sectionsHook.closeLoadTemplate}
            onApply={handleApplyTemplate}
          />
        )}

        {sectionsHook.activeSaveType && (
            <SaveTemplateModal
                open={!!sectionsHook.activeSaveType}
                type={sectionsHook.activeSaveType}
                onClose={sectionsHook.closeSaveTemplate}
                onSave={async (templateData) => {
                  const activeType = sectionsHook.activeSaveType as
                      | 'complaint'
                      | 'medicine'
                      | 'investigation'
                      | 'advice'
                      | 'diagnosis'
                      | 'quick_notes';

                  const currentData = getTemplateData(activeType);

                  const finalValue =
                      ['advice', 'diagnosis', 'quick_notes'].includes(activeType)
                          ? { value: String(currentData ?? '').trim() }
                          : currentData;

                  await sectionsHook.saveTemplate({
                    ...templateData,
                    type: activeType,
                    value: finalValue
                  });
                }}
            />
        )}
      </div>
    </FormProvider>
  );
} 